/**
 * 1on1 の進め方の振り返り（リーダー向け）
 *
 * 教科書 3-6「1on1のやり方」の基準に照らした観察と提案を出す。**点数も合否も出さない。**
 * 上長も読むことを画面に明記し、リーダーがこの回の背景を書き添えられるようにする
 * （機械の読みだけが上長に渡る状態にしないため）。
 *
 * メンバーには API が返さないので、このコンポーネントはリーダー権限のときだけ描画する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering, HR
 * @graph-business 1on1 の進め方についてリーダーに返す観察・提案・発言バランスの表示と背景メモの編集
 * @graph-connects /one-on-one/pairs/sessions [calls] 背景メモの保存
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import {
  ONE_ON_ONE_LEADER_SHARE_TARGET_PERCENT,
  type OneOnOneSessionResponse,
  type OneOnOneSpeakingBalance,
} from '@cortex/domain-product-innovation-lab-types';
import { ApiError } from '../../../lib/api.js';
import {
  useRegenerateLeaderFeedback,
  useUpdateOneOnOneSession,
} from '../../../hooks/useOneOnOne.js';

/**
 * API エラーを利用者向け文言にする
 * @graph-connects none
 */
function regenerateErrorMessageOf(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    return error.message || fallback;
  }
  return fallback;
}

/** @graph-connects none */
interface OneOnOneLeaderFeedbackProps {
  pairId: string;
  session: OneOnOneSessionResponse;
}

/**
 * 発言バランスの帯。目安（20%）を超えているかが一目で分かればよいので、数値も併記する。
 * @graph-connects none
 */
function SpeakingBalanceBar({ balance }: { balance: OneOnOneSpeakingBalance }) {
  const { t } = useTranslation();
  const leader = balance.leaderSharePercent;
  const minutes =
    balance.durationSeconds === null ? null : Math.round(balance.durationSeconds / 60);

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-xs font-medium text-[var(--color-text-muted)]">
          {t('oneOnOne.leaderFeedback.balance')}
        </p>
        <p className="text-xs text-[var(--color-text-muted)]">
          {t('oneOnOne.leaderFeedback.balanceTarget', {
            target: ONE_ON_ONE_LEADER_SHARE_TARGET_PERCENT,
          })}
          {minutes !== null && ` / ${t('oneOnOne.leaderFeedback.duration', { minutes })}`}
        </p>
      </div>
      <div
        className="mt-1 flex h-6 overflow-hidden rounded"
        role="img"
        aria-label={t('oneOnOne.leaderFeedback.balanceLabel', {
          leader,
          member: 100 - leader,
        })}
      >
        <div
          className="flex items-center justify-center bg-[var(--color-text-muted)] text-[11px] font-semibold text-white"
          style={{ flex: `0 0 ${String(leader)}%` }}
        >
          {leader >= 12 && `${String(leader)}%`}
        </div>
        <div className="flex flex-1 items-center justify-center bg-[var(--color-accent-primary)] text-[11px] font-semibold text-white">
          {`${String(100 - leader)}%`}
        </div>
      </div>
      <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
        {t('oneOnOne.leaderFeedback.balanceLegend', { leader, member: 100 - leader })}
      </p>
    </div>
  );
}

/**
 * 作られなかった回の説明。失敗ではないので淡々と理由だけ出す。
 * @graph-connects none
 */
function NotGenerated({ session }: { session: OneOnOneSessionResponse }) {
  const { t } = useTranslation();
  const status = session.leaderFeedbackStatus;
  if (status === undefined || status === 'generated') {
    return null;
  }
  return (
    <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
      {status === 'pending'
        ? t('oneOnOne.leaderFeedback.pending')
        : (session.leaderFeedbackReason ?? t('oneOnOne.leaderFeedback.unavailable'))}
    </p>
  );
}

/** @graph-connects none */
export function OneOnOneLeaderFeedback({ pairId, session }: OneOnOneLeaderFeedbackProps) {
  const { t } = useTranslation();
  const update = useUpdateOneOnOneSession();
  const regenerate = useRegenerateLeaderFeedback();
  const [note, setNote] = useState(session.leaderNote ?? '');
  const [noteDirty, setNoteDirty] = useState(false);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);
  const feedback = session.leaderFeedback;

  if (session.leaderFeedbackStatus === undefined) {
    return null;
  }

  const saveNote = () => {
    update.mutate(
      { pairId, sessionId: session.sessionId, body: { leaderNote: note } },
      {
        onSuccess: () => {
          setNoteDirty(false);
        },
      },
    );
  };

  return (
    <section
      className="mt-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] p-3"
      data-testid={`one-on-one-leader-feedback-${session.sessionId}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('oneOnOne.leaderFeedback.title')}
        </h4>
        <Badge variant="info">{t('oneOnOne.leaderFeedback.audience')}</Badge>
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto"
          disabled={regenerate.isPending}
          onClick={() => {
            setRegenerateError(null);
            regenerate.mutate(
              { pairId, sessionId: session.sessionId },
              {
                onError: (error) => {
                  setRegenerateError(
                    regenerateErrorMessageOf(error, t('oneOnOne.leaderFeedback.regenerateFailed')),
                  );
                },
              },
            );
          }}
        >
          {regenerate.isPending
            ? t('oneOnOne.leaderFeedback.regenerating')
            : t('oneOnOne.leaderFeedback.regenerate')}
        </Button>
      </div>
      <p className="mt-1 text-xs text-[var(--color-text-muted)]">
        {t('oneOnOne.leaderFeedback.notice')}
      </p>
      {regenerateError && (
        <p role="alert" className="mt-1 text-sm text-[var(--color-error)]">
          {regenerateError}
        </p>
      )}

      <NotGenerated session={session} />

      {feedback && (
        <div className="mt-3 space-y-3">
          {feedback.speakingBalance && <SpeakingBalanceBar balance={feedback.speakingBalance} />}

          {feedback.observations.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[var(--color-text-muted)]">
                {t('oneOnOne.leaderFeedback.observations')}
              </p>
              <ul className="mt-1 space-y-2">
                {feedback.observations.map((observation) => (
                  <li key={observation.frameworkKey} className="text-sm">
                    <span className="font-medium text-[var(--color-text-primary)]">
                      {observation.frameworkName ?? observation.frameworkKey}
                    </span>
                    <p className="text-[var(--color-text-primary)]">{observation.observation}</p>
                    <p className="text-xs text-[var(--color-text-muted)]">
                      {t('oneOnOne.leaderFeedback.basis', { basis: observation.basis })}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {feedback.suggestions.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[var(--color-text-muted)]">
                {t('oneOnOne.leaderFeedback.suggestions')}
              </p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-[var(--color-text-primary)]">
                {feedback.suggestions.map((suggestion) => (
                  <li key={suggestion.slice(0, 32)}>{suggestion}</li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-[11px] text-[var(--color-text-muted)]">
            {t('oneOnOne.leaderFeedback.source', { chapterId: feedback.chapterId })}
          </p>
        </div>
      )}

      <label className="mt-3 block text-xs font-medium text-[var(--color-text-muted)]">
        {t('oneOnOne.leaderFeedback.note')}
        <Textarea
          className="mt-1"
          rows={2}
          value={note}
          placeholder={t('oneOnOne.leaderFeedback.notePlaceholder')}
          onChange={(event) => {
            setNote(event.target.value);
            setNoteDirty(true);
          }}
        />
      </label>
      <div className="mt-2 flex justify-end">
        <Button
          size="sm"
          variant="secondary"
          disabled={!noteDirty || update.isPending}
          onClick={saveNote}
        >
          {t('oneOnOne.leaderFeedback.saveNote')}
        </Button>
      </div>
    </section>
  );
}
